import { useParams, useLocation, useNavigate } from "react-router-dom";
import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Match } from "../../models/Match";
import { Team } from "../../models/Team";
import { UpcomingMatch } from "../../models/UpcomingMatch";
import { getMatchesForPlayerBySeason, getUpcomingMatchesForTeam } from "../../services/MatchService";
import MatchTable from "../../components/MatchTabel";
import UpcomingMatchList from "./UpcomingMatchList";
import { fetchSeasonsPlayed } from "../../services/PlayerStatService";
import SeasonScroll from "../../components/SeasonScroll/SeasonScroll";
import Loading from "../../components/Loading";
import { useDarkMode } from '../../hooks/useDarkmode';
import styles from './PlayerMatch.module.css';

interface PlayerMatchProps {
  /** The player's current team, used to look up their fixtures. Without it, only played matches show. */
  team?: Team | null;
}

const PlayerMatch = ({ team }: PlayerMatchProps) => {
  const { steamId } = useParams<{ steamId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const [matches, setMatches] = useState<Match[]>([]);
  const [loading, setLoading] = useState(true);
  const [matchesLoading, setMatchesLoading] = useState(false);
  const [seasons, setSeasons] = useState<number[]>([]);
  const isDark = useDarkMode();
  const { t } = useTranslation('pages');

  // Played vs upcoming. Upcoming comes from the player's current team - the fixture list
  // that used to mean a detour to the team page.
  const [mode, setMode] = useState<'played' | 'upcoming'>('played');
  const [upcoming, setUpcoming] = useState<UpcomingMatch[]>([]);
  
  // Use a ref to track the current active request
  const activeRequestRef = useRef<number | null>(null);
  
  // Debug counter to track re-renders
  const renderCount = useRef(0);
  
  // Debug log
  console.log(`Render #${++renderCount.current} - Season: ${location.search}, Loading: ${matchesLoading}, Matches: ${matches.length}`);
  
  // Get the season from URL params or sessionStorage
  const getInitialSeason = () => {
    const params = new URLSearchParams(location.search);
    const seasonParam = params.get('season');
    if (seasonParam) return Number(seasonParam);
    
    const storedSeason = sessionStorage.getItem(`player_${steamId}_season`);
    return storedSeason ? Number(storedSeason) : 0;
  };
  
  const [season, setSeason] = useState<number>(() => getInitialSeason());

  // Update season handler with URL and storage update
  const handleSeasonChange = (newSeason: number) => {
    console.log(`handleSeasonChange called: ${season} -> ${newSeason}, loading: ${matchesLoading}`);
    
    // Skip if we're already on this season and it's loading
    if (newSeason === season && matchesLoading) {
      console.log("Season change skipped - already loading");
      return;
    }
    
    // Skip if season hasn't changed
    if (newSeason === season) {
      console.log("Season change skipped - same season");
      return;
    }
    
    console.log("Changing season and updating URL");
    
    // IMPORTANT: Clear matches immediately when changing seasons
    // This is the only way to avoid duplicate keys
    setMatches([]);
    
    setSeason(newSeason);
    setMatchesLoading(true);
    
    // Update URL with the new season
    const params = new URLSearchParams(location.search);
    params.set('season', newSeason.toString());
    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
    
    // Store in sessionStorage
    sessionStorage.setItem(`player_${steamId}_season`, newSeason.toString());
  };

  // Fetch which seasons the player has played - memoized to prevent excessive calls
  const fetchPlayedSeasons = useCallback(async () => {
    if (!steamId) return;
    
    console.log("fetchPlayedSeasons called");
    try {
      const fetchedSeasons = await fetchSeasonsPlayed(steamId);
      // Sort descending (highest season first)
      const sortedSeasons = fetchedSeasons.sort((a, b) => b - a);
      setSeasons(sortedSeasons);
      console.log(`Fetched ${sortedSeasons.length} seasons: ${sortedSeasons.join(', ')}`);

      // If no season is selected yet, default to the highest season
      if (sortedSeasons.length > 0 && season === 0) {
        console.log(`Setting initial season to ${sortedSeasons[0]}`);
        handleSeasonChange(sortedSeasons[0]);
      } else if (sortedSeasons.length === 0) {
        // Nothing to fetch, so the matches effect never clears this - a player who has not
        // played a season would otherwise sit on a spinner forever.
        setLoading(false);
      }
    } catch (error) {
      console.error("Error fetching seasons:", error);
      // 404s for a player who has never played a season - same dead end as an empty list.
      setLoading(false);
    }
  }, [steamId]); // Remove 'season' from dependency array to prevent re-fetch loop

  // On component mount, fetch all played seasons
  useEffect(() => {
    console.log("Initial seasons fetch effect triggered");
    fetchPlayedSeasons();
  }, [fetchPlayedSeasons]);

  // Whenever the selected season changes, fetch that season's matches
  useEffect(() => {
    if (!steamId || !season) {
      console.log("Skipping matches fetch - no steamId or season");
      return;
    }
    
    console.log(`Matches fetch effect triggered for season ${season}`);
    setMatchesLoading(true);
    
    // Generate a unique ID for this request
    const requestId = Date.now();
    activeRequestRef.current = requestId;
    console.log(`Starting request ${requestId} for season ${season}`);
    
    const fetchMatches = async () => {
      try {
        console.log(`Fetching matches for season ${season}...`);
        const fetchedMatches = await getMatchesForPlayerBySeason(steamId, season);
        
        // Only update state if this is still the active request
        if (activeRequestRef.current === requestId) {
          console.log(`Request ${requestId} completed: ${fetchedMatches.length} matches found`);
          
          // Clear and replace in a single render to avoid flashes
          setMatches(fetchedMatches); 
          setMatchesLoading(false);
          setLoading(false);
        } else {
          console.log(`Request ${requestId} ignored - no longer active`);
        }
      } catch (error) {
        console.error(`Error fetching matches for season ${season}:`, error);
        
        // Only update loading state if this is still the active request
        if (activeRequestRef.current === requestId) {
          console.log(`Request ${requestId} error handling`);
          setMatches([]); // Clear on error
          setMatchesLoading(false);
          setLoading(false);
        }
      }
    };
    
    fetchMatches();
    
    // Cleanup function to handle component unmount or dependency changes
    return () => {
      // Mark any in-progress request as stale
      console.log(`Cleaning up request ${activeRequestRef.current} for season ${season}`);
      if (activeRequestRef.current === requestId) {
        activeRequestRef.current = null;
      }
    };
  }, [season, steamId]);

  // The team's fixtures. Failure is silent: an empty list simply means no switch is offered and
  // the tab keeps working exactly as it did before.
  useEffect(() => {
    const teamId = team?.teamId;
    if (!teamId) {
      setUpcoming([]);
      return;
    }

    let cancelled = false;
    getUpcomingMatchesForTeam(teamId)
      .then(fetched => { if (!cancelled) setUpcoming(fetched ?? []); })
      .catch(() => { if (!cancelled) setUpcoming([]); });

    return () => { cancelled = true; };
  }, [team?.teamId]);

  // A team in a qualifier plays across two divisions at once; badge them then, as the team page does.
  const showUpcomingDivision = useMemo(
    () => new Set(upcoming.map(m => m.division).filter(Boolean)).size > 1,
    [upcoming]
  );

  // No fixtures means no switch at all, so a player without one sees the tab unchanged.
  const hasUpcoming = upcoming.length > 0;
  const activeMode = hasUpcoming ? mode : 'played';

  const showSeasons = activeMode === 'played' && seasons.length > 0;

  return (
    <div>
      {hasUpcoming && (
        <div className={styles.modeGroup} role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={activeMode === 'played'}
            className={`${styles.modePill} ${activeMode === 'played' ? styles.modePillActive : ''}`}
            onClick={() => setMode('played')}
          >
            {t('matches.results')}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeMode === 'upcoming'}
            className={`${styles.modePill} ${activeMode === 'upcoming' ? styles.modePillActive : ''}`}
            onClick={() => setMode('upcoming')}
          >
            {t('matches.upcoming')}
            <span className={styles.modeCount}>{upcoming.length}</span>
          </button>
        </div>
      )}

      {/* Only render the SeasonScroll if we have seasons */}
      {showSeasons && (
        <SeasonScroll
          seasons={seasons}
          selectedSeason={season}
          onSeasonChange={handleSeasonChange}
          isLoading={matchesLoading}
        />
      )}

      {/* The team-page season, not the picked one - these links belong to the running season. */}
      {activeMode === 'upcoming' && (
        <UpcomingMatchList
          matches={upcoming}
          season={team?.season}
          showDivision={showUpcomingDivision}
        />
      )}

      {/* Show a loading indicator when matches are loading but matches array is empty */}
      {activeMode === 'played' && (matchesLoading && matches.length === 0 ? (
        <div className="position-relative" style={{ minHeight: "200px" }}>
          <div 
            className="d-flex align-items-center justify-content-center"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 10
            }}
          >
            <div 
              className="d-flex align-items-center justify-content-center"
              style={{
                width: '80px',
                height: '80px',
                borderRadius: '10px',
                backgroundColor: isDark ? 'rgba(33, 37, 41, 0.7)' : 'rgba(255, 255, 255, 0.7)',
                boxShadow: isDark ? '0 0 15px rgba(0, 0, 0, 0.5)' : '0 0 15px rgba(0, 0, 0, 0.2)'
              }}
            >
              <div className={`spinner-border ${isDark ? 'text-light' : 'text-primary'}`} role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          </div>
        </div>
      ) : loading && matches.length === 0 ? (
        <Loading />
      ) : (
        // IMPORTANT: Use the key prop to force complete re-rendering of the table when season changes
        // This prevents React from reusing DOM elements with duplicate keys
        <div key={`season-${season}`}>
          <MatchTable 
            matches={matches} 
            compact={false} 
            season={season} 
          />
        </div>
      ))}
    </div>
  );
};

export default PlayerMatch;